import { useEffect } from "react";

const DEFAULTS = {
  title: "Pikra - Tickets con alma",
  description:
    "Tickets digitales animados para eventos, pagos con Stripe Connect y validacion QR.",
  image: "/favicon.svg",
  type: "website",
  siteName: "Pikra",
};

function ensureMeta(selector, createAttrs) {
  let el = document.head.querySelector(selector);
  let created = false;
  if (!el) {
    el = document.createElement("meta");
    for (const [k, v] of Object.entries(createAttrs)) el.setAttribute(k, v);
    document.head.appendChild(el);
    created = true;
  }
  return { el, created };
}

function setOG(property, content) {
  return ensureMeta(
    `meta[property="${property}"]`,
    { property }
  ).el && document.head.querySelector(`meta[property="${property}"]`).setAttribute("content", content);
}

function setName(name, content) {
  return ensureMeta(
    `meta[name="${name}"]`,
    { name }
  ).el && document.head.querySelector(`meta[name="${name}"]`).setAttribute("content", content);
}

function setCanonical(url) {
  let el = document.head.querySelector('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", url);
}

/**
 * Synchronizes <head> metadata for the current page.
 * Renders nothing. Side-effects only.
 *
 * Props (all optional, fall back to brand defaults):
 *   title       - page title (becomes "<title> | Pikra" unless title === brand)
 *   description - meta description / OG description
 *   image       - absolute or relative URL for og:image and twitter:image
 *   url         - canonical URL (defaults to current location)
 *   type        - og:type (default "website")
 */
export default function SEOHead({ title, description, image, url, type }) {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const resolvedTitle = title
      ? `${title} | ${DEFAULTS.siteName}`
      : DEFAULTS.title;
    const resolvedDescription = description || DEFAULTS.description;
    const resolvedImage = image || DEFAULTS.image;
    const resolvedUrl =
      url ||
      (typeof window !== "undefined" ? window.location.href : "");
    const resolvedType = type || DEFAULTS.type;

    // Snapshot previous values so we can restore on unmount and avoid leaking
    // metadata from one route into the next.
    const previousTitle = document.title;
    document.title = resolvedTitle;

    setName("description", resolvedDescription);

    setOG("og:title", resolvedTitle);
    setOG("og:description", resolvedDescription);
    setOG("og:image", resolvedImage);
    setOG("og:url", resolvedUrl);
    setOG("og:type", resolvedType);
    setOG("og:site_name", DEFAULTS.siteName);

    setName("twitter:card", "summary_large_image");
    setName("twitter:title", resolvedTitle);
    setName("twitter:description", resolvedDescription);
    setName("twitter:image", resolvedImage);

    setCanonical(resolvedUrl);

    return () => {
      document.title = previousTitle;
    };
  }, [title, description, image, url, type]);

  return null;
}
